import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { blogadd, blogCategories, blogUploadImage } from './proxyurls';
import Select from 'react-select';
import Layout from './layout';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';

function AddBlog() {
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        metakeywords: '',
        metatitle: '',
        meta_description: '',
        canonical: '',
        alttext: '',
        status: 0,
        category: '',
        image: null,
        content: '',
        publish_datetime: new Date().toISOString().split('T')[0]
    });
    const [formErrors, setFormErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [categories, setCategories] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const navigate = useNavigate();
    const editorRef = useRef(null);
    const skipLinkFollowPromptRef = useRef(false);
    const [showLinkFollowModal, setShowLinkFollowModal] = useState(false);
    const [showAltTextModal, setShowAltTextModal] = useState(false);
    const [currentImageAltText, setCurrentImageAltText] = useState('');
    const lastUploadedEditorImageUrlRef = useRef(null);

    // Function to generate slug from title
    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '') // Remove special characters
            .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
            .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    };

    // Fetch categories on component mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get(blogCategories);
                const categoriesData = Array.isArray(response.data) ? response.data : (response.data.categories || response.data.data || []);
                setCategories(categoriesData);
                
                // Convert to react-select format
                const options = categoriesData.map(category => ({
                    value: category.id || category._id || category.name,
                    label: category.name || category.title || category.label
                }));
                setCategoryOptions(options);
            } catch (error) {
                console.error('Error fetching categories:', error);
                setErrorMessage('Error fetching categories');
                setTimeout(() => setErrorMessage(''), 3000);
            }
        };

        fetchCategories();
    }, []);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'image' && files && files[0]) {
            const file = files[0];
            setFormData(prevData => ({
                ...prevData,
                [name]: file
            }));
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else if (name === 'name') {
            // When title changes, auto-generate slug if it hasn't been manually edited
            setFormData(prevData => ({
                ...prevData,
                [name]: value,
                slug: slugManuallyEdited ? prevData.slug : generateSlug(value)
            }));
        } else if (name === 'slug') {
            // Track if slug is manually edited
            setSlugManuallyEdited(true);
            setFormData(prevData => ({
                ...prevData,
                [name]: value
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                [name]: value
            }));
        }
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        setFormData(prevData => ({
            ...prevData,
            [fieldName]: selectedOption ? selectedOption.value : ''
        }));
    };

    const handleDateChange = (date, fieldName) => {
        if (date) {
            // Format as date string (YYYY-MM-DD)
            const dateString = date.toISOString().split('T')[0];
            setFormData(prevData => ({
                ...prevData,
                [fieldName]: dateString
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                [fieldName]: ''
            }));
        }
    };

    const handleContentChange = (event, editor) => {
        const data = editor.getData();
        setFormData(prevData => ({
            ...prevData,
            content: data
        }));
    };

    const applyLinkFollowChoice = (useNofollow) => {
        const editor = editorRef.current;
        if (!editor) {
            setShowLinkFollowModal(false);
            return;
        }
        const href = editor.commands.get('link').value;
        if (typeof href !== 'string' || href === '') {
            setShowLinkFollowModal(false);
            return;
        }
        skipLinkFollowPromptRef.current = true;
        try {
            editor.execute('link', href, {
                linkNofollow: useNofollow,
                linkDofollow: !useNofollow,
            });
        } finally {
            skipLinkFollowPromptRef.current = false;
        }
        setFormData(prev => ({
            ...prev,
            content: editor.getData(),
        }));
        setShowLinkFollowModal(false);
    };

    const registerLinkFollowPrompt = (editor) => {
        const linkCmd = editor.commands.get('link');
        if (!linkCmd) return;

        linkCmd.on('execute', () => {
            if (skipLinkFollowPromptRef.current) return;

            setTimeout(() => {
                if (skipLinkFollowPromptRef.current) return;
                const href = editor.commands.get('link').value;
                if (typeof href !== 'string' || href === '') return;
                setShowLinkFollowModal(true);
            }, 0);
        }, { priority: 'lowest' });
    };

    const closeAltTextModal = () => {
        setShowAltTextModal(false);
        setCurrentImageAltText('');
        lastUploadedEditorImageUrlRef.current = null;
    };

    const handleAltTextSubmit = () => {
        const editor = editorRef.current;
        if (editor) {
            const editorData = editor.getData();
            const parser = new DOMParser();
            const doc = parser.parseFromString(editorData, 'text/html');
            const images = doc.querySelectorAll('img');
            const targetUrl = lastUploadedEditorImageUrlRef.current;

            if (images.length > 0) {
                let targetImg = null;
                if (targetUrl) {
                    for (let i = images.length - 1; i >= 0; i--) {
                        const src = images[i].getAttribute('src') || '';
                        if (src === targetUrl || src.includes(targetUrl) || targetUrl.includes(src)) {
                            targetImg = images[i];
                            break;
                        }
                    }
                }
                if (!targetImg) {
                    targetImg = images[images.length - 1];
                }
                if (targetImg) {
                    targetImg.setAttribute('alt', currentImageAltText);
                    const nextHtml = doc.body.innerHTML;
                    editor.setData(nextHtml);
                    setFormData(prev => ({ ...prev, content: nextHtml }));
                }
            }
        }

        closeAltTextModal();
    };

    // CKEditor upload adapter for images
    const uploadAdapterPlugin = (editor) => {
        try {
            const fileRepository = editor.plugins.get('FileRepository');
            if (!fileRepository) {
                console.error('FileRepository plugin not found');
                return;
            }

            fileRepository.createUploadAdapter = (loader) => {
                return {
                    upload: () => {
                        if (!loader || !loader.file) {
                            return Promise.reject(new Error('File loader is not available'));
                        }

                        return loader.file.then(file => {
                            if (!file) {
                                return Promise.reject(new Error('No file selected'));
                            }

                            // Validate file type
                            if (!file.type || !file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
                                const errorMsg = 'Invalid image format. Please use JPEG, PNG, GIF, or WebP';
                                setErrorMessage(errorMsg);
                                setTimeout(() => setErrorMessage(''), 5000);
                                return Promise.reject(new Error(errorMsg));
                            }

                            // Upload image to backend
                            const imageFormData = new FormData();
                            imageFormData.append('image', file);

                            return axios.post(blogUploadImage, imageFormData, {
                                withCredentials: true
                            }).then(response => {
                                // Get imageUrl from response (handle different response formats)
                                const imageUrl = response.data?.imageUrl || 
                                              response.data?.url || 
                                              response.data?.image || 
                                              response.data?.data?.imageUrl ||
                                              response.data?.data?.url ||
                                              '';
                                
                                if (!imageUrl) {
                                    const errorMsg = 'Failed to get image URL from upload response';
                                    setErrorMessage(errorMsg);
                                    setTimeout(() => setErrorMessage(''), 5000);
                                    return Promise.reject(new Error(errorMsg));
                                }

                                setSuccessMessage('Image uploaded successfully');
                                setTimeout(() => setSuccessMessage(''), 3000);

                                lastUploadedEditorImageUrlRef.current = imageUrl;
                                setTimeout(() => {
                                    setShowAltTextModal(true);
                                }, 500);

                                // Return the image URL to CKEditor
                                return {
                                    default: imageUrl
                                };
                            }).catch(error => {
                                const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Error uploading image';
                                setErrorMessage(errorMsg);
                                setTimeout(() => setErrorMessage(''), 5000);
                                return Promise.reject(new Error(errorMsg));
                            });
                        }).catch(error => {
                            const errorMsg = error.message || 'Error loading file';
                            setErrorMessage(errorMsg);
                            setTimeout(() => setErrorMessage(''), 5000);
                            return Promise.reject(new Error(errorMsg));
                        });
                    },
                    abort: () => {
                        // Handle upload cancellation if needed
                    }
                };
            };
        } catch (error) {
            console.error('Error setting up upload adapter:', error);
            setErrorMessage('Image upload feature not available.');
            setTimeout(() => setErrorMessage(''), 5000);
        }
    };

    // CKEditor configuration
    const editorConfiguration = {
        toolbar: [
            'heading',
            '|',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'outdent',
            'indent',
            '|',
            'link',
            'uploadImage',
            '|',
            'blockQuote',
            'insertTable',
            '|',
            'undo',
            'redo'
        ],
        heading: {
            options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
                { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
            ]
        },
        image: {
            toolbar: [
                'imageTextAlternative',
                'toggleImageCaption',
                'imageStyle:inline',
                'imageStyle:block',
                'imageStyle:side'
            ],
            upload: {
                types: ['jpeg', 'jpg', 'png', 'gif', 'webp']
            },
            insert: {
                integrations: ['upload', 'url']
            },
            resizeUnit: 'px',
            styles: ['full', 'side', 'alignLeft', 'alignCenter', 'alignRight']
        },
        link: {
            decorators: {
                nofollow: {
                    mode: 'manual',
                    label: 'No follow',
                    defaultValue: false,
                    attributes: {
                        rel: 'nofollow',
                    },
                },
                dofollow: {
                    mode: 'manual',
                    label: 'Do follow',
                    defaultValue: false,
                    attributes: {
                        'data-link-follow': 'dofollow',
                    },
                },
            },
        },
        simpleUpload: {
            uploadUrl: blogUploadImage,
            withCredentials: true,
        },
    };

    const validatePublishForm = () => {
        const errors = {};
        if (!formData.name?.trim()) errors.name = 'Title is required';
        if (!formData.slug?.trim()) errors.slug = 'Slug is required';
        if (!formData.content?.trim()) errors.content = 'Content is required';
        return errors;
    };

    const validateDraftForm = () => {
        const errors = {};
        if (!formData.name?.trim()) errors.name = 'Title is required';
        return errors;
    };

    const performSubmit = async (asDraft) => {
        const validationErrors = asDraft ? validateDraftForm() : validatePublishForm();
        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            return;
        }
        setFormErrors({});

        const slugForSubmit = (formData.slug?.trim() || generateSlug(formData.name) || `draft-${Date.now()}`).trim();
        const statusValue = asDraft ? 1 : 0;

        try {
            let imageUrl = '';

            if (formData.image) {
                const imageFormData = new FormData();
                imageFormData.append('image', formData.image);

                const uploadResponse = await axios.post(blogUploadImage, imageFormData, {
                    withCredentials: true
                });

                imageUrl = uploadResponse.data?.imageUrl ||
                          uploadResponse.data?.url ||
                          uploadResponse.data?.image ||
                          uploadResponse.data?.data?.imageUrl ||
                          uploadResponse.data?.data?.url ||
                          '';

                if (!imageUrl) {
                    setErrorMessage('Failed to get image URL from upload response');
                    return;
                }
            }

            const submitData = new FormData();
            submitData.append('name', formData.name.trim());
            submitData.append('slug', slugForSubmit);
            submitData.append('meta_keywords', formData.metakeywords);
            submitData.append('meta_title', formData.metatitle);
            submitData.append('meta_description', formData.meta_description);
            submitData.append('cannonical_link', formData.canonical);
            submitData.append('alttext', formData.alttext);
            submitData.append('status', statusValue);
            submitData.append('category', formData.category);
            submitData.append('content', formData.content || '');
            submitData.append('imagetype', 3);
            submitData.append('type', 'vls');
            submitData.append('publish_datetime', formData.publish_datetime || '');
            if (imageUrl) {
                submitData.append('featured_image', imageUrl);
            }

            await axios.post(blogadd, submitData, {
                headers: {
                    'Content-Type': 'application/json',
                },
                withCredentials: true
            });

            const msg = asDraft ? 'Blog saved as draft' : 'Blog published successfully';
            setSuccessMessage(msg);
            setErrorMessage('');
            setFormData(prev => ({ ...prev, status: statusValue, slug: slugForSubmit }));
            localStorage.setItem('successMessage', msg);
            setTimeout(() => {
                navigate('/manage-blogs');
                setSuccessMessage('');
            }, 1000);
        } catch (error) {
            console.error('Error adding blog:', error);
            setErrorMessage(error.response?.data?.error || error.response?.data?.message || 'An error occurred');
        }
    };

    const handlePublishSubmit = (e) => {
        e.preventDefault();
        performSubmit(false);
    };

    const handleCancel = () => {
        navigate('/manage-blogs');
    };

    return (
        <Layout>
            <div style={{ padding: '8px' }}>
                {successMessage && <div className="alert alert-success">{successMessage}</div>}
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                <h3 className='py-3 px-3 border-bottom'>Add Blog</h3>
                <form onSubmit={handlePublishSubmit} className='py-3 px-3'>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="name" className="form-label">Title</label>
                            <input
                                type="text"
                                id="name"
                                className="form-control"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                            />
                            {formErrors.name && <div className="text-danger">{formErrors.name}</div>}
                        </div>
                        <div className="col-6">
                            <label htmlFor="slug" className="form-label">Slug</label>
                            <input
                                type="text"
                                id="slug"
                                className="form-control"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                            />
                            {formErrors.slug && <div className="text-danger">{formErrors.slug}</div>}
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="metatitle" className="form-label">Meta Title</label>
                            <input
                                type="text"
                                id="metatitle"
                                className="form-control"
                                name="metatitle"
                                value={formData.metatitle}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="metakeywords" className="form-label">Meta Keywords</label>
                            <input
                                type="text"
                                id="metakeywords"
                                className="form-control"
                                name="metakeywords"
                                value={formData.metakeywords}
                                onChange={handleChange}
                                placeholder="comma separated keywords"
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-12">
                            <label htmlFor="meta_description" className="form-label">Meta Description</label>
                            <textarea
                                id="meta_description"
                                className="form-control"
                                name="meta_description"
                                rows="3"
                                value={formData.meta_description}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="canonical" className="form-label">Canonical</label>
                            <input
                                type="text"
                                id="canonical"
                                className="form-control"
                                name="canonical"
                                value={formData.canonical}
                                onChange={handleChange}
                                placeholder="https://example.com/blog-post"
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="category" className="form-label">Category</label>
                            <Select
                                id="category"
                                name="category"
                                options={categoryOptions}
                                value={categoryOptions.find(option => option.value === formData.category || option.value === String(formData.category))}
                                onChange={option => handleSelectChange(option, 'category')}
                                placeholder="Select a category"
                                isClearable
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="image" className="form-label">Image (1000*667)</label>
                            <input
                                type="file"
                                id="image"
                                className="form-control"
                                name="image"
                                accept="image/*"
                                onChange={handleChange}
                            />
                            {imagePreview && (
                                <div className="mt-2">
                                    <img src={imagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px' }} />
                                </div>
                            )}
                        </div>
                        <div className="col-6">
                            <label className="form-label d-block">&nbsp;</label>
                            <p className="small text-muted mb-0 pt-2">
                                <strong>Publish</strong> sets the post live (requires title, slug, and content).
                                <strong> Save as draft</strong> only requires a title; you can finish editing later.
                            </p>
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="alttext" className="form-label">Alt Text</label>
                            <input
                                type="text"
                                id="alttext"
                                className="form-control"
                                name="alttext"
                                value={formData.alttext}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="publish_datetime" className="form-label">Publish Date & Time</label>
                            <DatePicker
                                id="publish_datetime"
                                className="form-control"
                                selected={formData.publish_datetime ? new Date(formData.publish_datetime) : new Date()}
                                onChange={date => handleDateChange(date, 'publish_datetime')}
                                dateFormat="yyyy-MM-dd"
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-12">
                            <label htmlFor="content" className="form-label">Content</label>
                            <style>{`
                                .ck-editor__editable {
                                    min-height: 300px;
                                }
                                .ck.ck-editor__main>.ck-editor__editable:not(.ck-focused) {
                                    height: 300px;
                                }
                                .ck-content {
                                    height: 300px;
                                }
                            `}</style>
                            <CKEditor
                                editor={ClassicEditor}
                                config={editorConfiguration}
                                data={formData.content}
                                onReady={(editor) => {
                                    editorRef.current = editor;
                                    uploadAdapterPlugin(editor);
                                    registerLinkFollowPrompt(editor);
                                }}
                                onChange={handleContentChange}
                            />
                            {formErrors.content && <div className="text-danger">{formErrors.content}</div>}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12 d-flex flex-wrap gap-2 align-items-center">
                            <button type="submit" className="btn btn-primary">Publish</button>
                            <button type="button" className="btn btn-outline-secondary" onClick={() => performSubmit(true)}>
                                Save as draft
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
                        </div>
                    </div>
                </form>

                {showAltTextModal && (
                    <div
                        className="modal show"
                        style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                        tabIndex="-1"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="addBlogImageAltTitle"
                    >
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title" id="addBlogImageAltTitle">
                                        Alt text for uploaded image
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeAltTextModal}
                                        aria-label="Close"
                                    />
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="addBlogCkImageAlt" className="form-label">
                                            Alt text
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="addBlogCkImageAlt"
                                            value={currentImageAltText}
                                            onChange={(e) => setCurrentImageAltText(e.target.value)}
                                            placeholder="Describe the image for accessibility and SEO"
                                            autoFocus
                                        />
                                        <small className="form-text text-muted">
                                            Shown to screen readers and used when the image cannot load.
                                        </small>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeAltTextModal}>
                                        Skip
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={handleAltTextSubmit}>
                                        Save alt text
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default AddBlog;
